import { useEffect, useMemo, useRef, useState } from 'react';
import { useModal } from '../useModal';
import { pbToast } from '../toast';
import { FormFields, FormFooter, initialValuesFromFields } from './FormFields';
import type { FieldSpec } from './FormFields';
import type { ChartSettings, LabelMode, ArrowMode, SpinMode } from '../types';

const SUB_STYLE = { margin: '2px 0 0', fontWeight: 400, fontSize: '0.82em', opacity: 0.68 };

const THEME_DEFAULTS = { accent: '#0A5F8C', teal: '#0E7B72', slate: '#3A5068', bg: '#E9EFF6' };

function buildFields(settings: ChartSettings): FieldSpec[] {
  return [
    { key: 'title', label: 'Chart title', type: 'text', value: settings.title },
    { key: 'meta', label: 'Header meta line', type: 'text', value: settings.meta },

    { section: 'Labels & dots' },
    {
      key: 'labelMode', label: 'Default label style', type: 'select', value: settings.labelMode,
      options: [
        { value: 'auto', label: 'Side (auto left/right)' },
        { value: 'radial', label: 'Radial — along the spoke' },
        { value: 'tangent', label: 'Tangent — along the orbit' },
        { value: 'above', label: 'Above the dot' },
        { value: 'below', label: 'Below the dot' },
        { value: 'hidden', label: 'Hidden' },
      ],
    },
    { key: 'nameSize', label: 'Name size', type: 'number', value: settings.nameSize, half: true, min: 6, max: 26, step: 0.5 },
    { key: 'roleSize', label: 'Role size', type: 'number', value: settings.roleSize, half: true, min: 5, max: 22, step: 0.5 },
    { key: 'dotSize', label: 'Person dot size', type: 'number', value: settings.dotSize, half: true, min: 3, max: 16, step: 0.5 },
    { key: 'showRoles', label: 'Show role under each name', type: 'checkbox', value: settings.showRoles },

    { section: 'Chart elements' },
    { key: 'showRings', label: 'Show orbit rings', type: 'checkbox', value: settings.showRings },
    { key: 'showSpokes', label: 'Show connecting wires', type: 'checkbox', value: settings.showSpokes },
    { key: 'showPills', label: 'Show program FTE pills', type: 'checkbox', value: settings.showPills },
    { key: 'showVacBox', label: 'Show dashed vacancy boxes', type: 'checkbox', value: settings.showVacBox },
    { key: 'animate', label: 'Animate orbit rings', type: 'checkbox', value: settings.animate },

    { section: 'Connector lines (default for every wire)' },
    { key: 'wireColor', label: 'Line colour', type: 'color', value: settings.wireColor, half: true },
    { key: 'wireWidth', label: 'Line thickness', type: 'number', value: settings.wireWidth, half: true, min: 0.4, max: 14, step: 0.2 },
    { key: 'wireDash', label: 'Dash pattern', type: 'text', value: settings.wireDash, half: true, placeholder: 'solid' },
    {
      key: 'wireArrow', label: 'Arrowheads', type: 'select', value: settings.wireArrow, half: true,
      options: [
        { value: 'none', label: 'None' },
        { value: 'end', label: 'Arrow at the child' },
        { value: 'start', label: 'Arrow at the parent' },
        { value: 'both', label: 'Both ends' },
      ],
    },

    { section: 'Orbit rings (default for every ring)' },
    { key: 'ringWidth', label: 'Ring thickness', type: 'number', value: settings.ringWidth, half: true, min: 0.4, max: 14, step: 0.2 },
    { key: 'ringDash', label: 'Dash pattern', type: 'text', value: settings.ringDash, half: true, placeholder: '5 5' },
    { key: 'ringSpeed', label: 'Rotation seconds / cycle', type: 'number', value: settings.ringSpeed, half: true, min: 1, max: 200 },
    {
      key: 'ringSpin', label: 'Rotation direction', type: 'select', value: settings.ringSpin, half: true,
      options: [
        { value: 'normal', label: 'Clockwise' },
        { value: 'reverse', label: 'Counter-clockwise' },
        { value: 'off', label: 'No rotation' },
      ],
    },

    { section: 'Group & program discs (defaults)' },
    { key: 'groupFill', label: 'Group background', type: 'color', value: settings.groupFill, half: true },
    { key: 'groupText', label: 'Group text colour', type: 'color', value: settings.groupText, half: true },
    { key: 'groupFont', label: 'Group text size', type: 'number', value: settings.groupFont, half: true, min: 6, max: 44, step: 0.5 },
    { key: 'labelDist', label: 'Employee label offset', type: 'number', value: settings.labelDist, half: true, min: 4, max: 200 },
    { key: 'progFill', label: 'Program background', type: 'color', value: settings.progFill, half: true },
    { key: 'progText', label: 'Program text colour', type: 'color', value: settings.progText, half: true },
    { key: 'progFont', label: 'Program text size', type: 'number', value: settings.progFont, half: true, min: 6, max: 40, step: 0.5 },

    { section: 'Theme' },
    { key: 'accent', label: 'Accent (leadership)', type: 'color', value: settings.accent ?? THEME_DEFAULTS.accent, half: true },
    { key: 'teal', label: 'Group rings', type: 'color', value: settings.teal ?? THEME_DEFAULTS.teal, half: true },
    { key: 'slate', label: 'Program discs', type: 'color', value: settings.slate ?? THEME_DEFAULTS.slate, half: true },
    { key: 'bg', label: 'Background', type: 'color', value: settings.bg ?? THEME_DEFAULTS.bg, half: true },
  ];
}

interface ChartSettingsFormValues {
  title: string;
  meta: string;
  labelMode: LabelMode;
  nameSize: number | null;
  roleSize: number | null;
  dotSize: number | null;
  showRoles: boolean;
  showRings: boolean;
  showSpokes: boolean;
  showPills: boolean;
  showVacBox: boolean;
  animate: boolean;
  wireColor: string;
  wireWidth: number | null;
  wireDash: string;
  wireArrow: ArrowMode;
  ringWidth: number | null;
  ringDash: string;
  ringSpeed: number | null;
  ringSpin: SpinMode;
  groupFill: string;
  groupText: string;
  groupFont: number | null;
  labelDist: number | null;
  progFill: string;
  progText: string;
  progFont: number | null;
  accent: string;
  teal: string;
  slate: string;
  bg: string;
}

function validate(values: Record<string, boolean | number | null | string>, fields: FieldSpec[]): string[] {
  const errors: string[] = [];
  if (!String(values.title || '').trim()) errors.push('Chart title is required.');
  fields.forEach((field) => {
    if (field.section != null || field.type !== 'number') return;
    const key = field.key as string;
    const v = values[key] as number | null;
    if (v == null || Number.isNaN(v)) { errors.push(`${field.label} must be a number.`); return; }
    if (field.min != null && v < field.min) errors.push(`${field.label} must be at least ${field.min}.`);
    if (field.max != null && v > field.max) errors.push(`${field.label} must be at most ${field.max}.`);
  });
  return errors;
}

function applyThemeCssVars(patch: { accent?: string; teal?: string; slate?: string; bg?: string }) {
  if (typeof document === 'undefined') return;
  const root = document.getElementById('page-org');
  if (!root) return;
  if (patch.accent != null) root.style.setProperty('--gold', patch.accent);
  if (patch.teal != null) root.style.setProperty('--teal', patch.teal);
  if (patch.slate != null) root.style.setProperty('--slate', patch.slate);
  if (patch.bg != null) root.style.setProperty('--bg', patch.bg);
}

interface ChartSettingsEditorProps {
  settings: ChartSettings;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  dispatch: (action: any) => void;
  onClose: () => void;
}

export function ChartSettingsEditor({ settings, dispatch, onClose }: ChartSettingsEditorProps) {
  const { openModal, closeModal } = useModal();

  const fields = useMemo(() => buildFields(settings), [settings]);
  const [values, setValues] = useState(
    () => initialValuesFromFields(fields) as unknown as ChartSettingsFormValues,
  );
  const [error, setError] = useState<string | null>(null);
  const onChange = (key: string, value: boolean | number | null | string) =>
    setValues((v) => ({ ...v, [key]: value } as unknown as ChartSettingsFormValues));

  const idRef = useRef('org-chart-settings-editor');
  const id = idRef.current;

  useEffect(() => {
    applyThemeCssVars({
      accent: settings.accent ?? THEME_DEFAULTS.accent,
      teal: settings.teal ?? THEME_DEFAULTS.teal,
      slate: settings.slate ?? THEME_DEFAULTS.slate,
      bg: settings.bg ?? THEME_DEFAULTS.bg,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function handleSave() {
    setError(null);
    const errors = validate(values as unknown as Record<string, boolean | number | null | string>, fields);
    if (errors.length) {
      setError(errors.join('\n'));
      return;
    }
    const patch = { ...values, title: values.title.trim() };
    dispatch({ type: 'UPDATE_SETTINGS', patch });
    applyThemeCssVars(patch);
    pbToast('Settings applied', 'ok');
    closeModal(id);
  }

  function handleCancel() {
    closeModal(id);
  }

  useEffect(() => {
    openModal({
      id,
      pageScope: 'page-org',
      title: (
        <>
          <div>Chart settings</div>
          <div style={SUB_STYLE}>Typography, visibility and theme.</div>
        </>
      ),
      render: () => (
        <>
          <p className="merr" style={{ whiteSpace: 'pre-line' }} role="alert">{error || ''}</p>
          <FormFields fields={fields} values={values as unknown as Record<string, boolean | number | null | string>} onChange={onChange} idPrefix={`${id}-`} />
        </>
      ),
      footer: (
        <FormFooter
          saveLabel="Apply"
          onCancel={handleCancel}
          onSave={handleSave}
        />
      ),
      onClose,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, fields, values, error]);

  useEffect(() => () => closeModal(id), [id, closeModal]);

  return null;
}

export default ChartSettingsEditor;
